import React, {
  useState,
  useContext,
  createContext,
  useRef,
  useEffect,
} from "react";
import {
  ChevronRight,
  ChevronDown,
  Plus,
  FileText,
  Hash,
  Type,
  List,
  Calculator,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Image,
  Link,
  Sigma,
  Check,
  X,
  Trash2,
  Settings,
  AlertTriangle,
  Edit3,
} from "lucide-react";
import {
  ExamContext,
  PAPER_SIZES,
  InlineQuestionEditor,
  RichContentRenderer,
  EnhancedRichTextEditor,
  calculateRequiredPages,
  createNewQuestion,
  generateId, // ✅ ADD this import
  updateExaminationTemplateWithMetadata, // ✅ ADD this import
  // ✅ NEW: Import metadata management functions
  updateMetadataForSection,
  updateMetadataForQuestion,
  generateMetadataFromExam,
  validateQuestionMarks,
  validateTotalExamMarks,
} from "./utility.js";
import {
  updateQuestion,
  updateTemplateSection,
  updateExaminationTemplate,
  addQuestion,
} from "../../../services/Users-services.js";
// Add this import at the top with other imports
import CustomTimePicker from "./../Examination/DurationSelector.js"; // Adjust path as needed
import { template } from "lodash";
// Inline editable text component
// Updated InlineEditableText component in canvas.js
const InlineEditableText = ({
  value,
  onChange,
  className,
  isEditable = true,
  placeholder,
  isRichText = false,
  // ✅ NEW: Add metadata context props
  onMetadataChange = null,
  metadataContext = null,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (!isRichText) {
        inputRef.current.select();
      }
    }
  }, [isEditing, isRichText]);

  const handleSave = async () => {
    // ✅ FIXED: Don't update local state immediately, use API first
    try {
      // Update via API if metadata context is provided
      if (onMetadataChange && metadataContext) {
        await onMetadataChange(metadataContext, tempValue);
      } else {
        // Fallback to direct onChange
        onChange(tempValue);
      }
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving:", error);
      // Reset to original value on error
      setTempValue(value);
      setIsEditing(false);
    }
  };
  
  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !isRichText) {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleRichTextChange = (content) => {
    setTempValue(content);

    // ✅ NEW: Update metadata for rich text content
    if (onMetadataChange && metadataContext) {
      onMetadataChange(metadataContext, content);
    }
  };

  // ✅ UPDATED: Only allow editing if isEditable is true
  const handleClick = () => {
    if (isEditable) {
      setIsEditing(true);
    }
  };

  if (isEditing && isEditable) {
    if (isRichText) {
      return (
        <div className="inline-rich-editor-container">
          <EnhancedRichTextEditor
            value={tempValue}
            onChange={handleRichTextChange}
            placeholder={placeholder}
            showMathTools={false}
            onMetadataChange={onMetadataChange}
            metadataContext={metadataContext}
          />
          <div className="inline-editor-actions">
            <button onClick={handleSave} className="save-btn">
              <Check size={14} />
            </button>
            <button onClick={handleCancel} className="cancel-btn">
              <X size={14} />
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="inline-editor-container">
        <input
          ref={inputRef}
          type="text"
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onKeyDown={handleKeyPress}
          onBlur={handleSave}
          className={`inline-editor ${className}`}
          placeholder={placeholder}
        />
      </div>
    );
  }

  return (
    <div
      className={`inline-editable ${className} ${!isEditable ? 'non-editable' : ''}`}
      onClick={handleClick}
      title={isEditable ? "Click to edit" : undefined}
      style={{ cursor: isEditable ? 'pointer' : 'default' }}
    >
      {isRichText ? (
        <RichContentRenderer content={value} />
      ) : (
        value || placeholder
      )}
      {/* ✅ UPDATED: Only show edit icon if isEditable is true */}
      {isEditable && <Edit3 size={12} className="edit-icon" />}
    </div>
  );
};

// Replace the existing DurationPicker component in canvas.js with this fixed version
const DurationPicker = ({ value, onChange, placeholder, className }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [timeValues, setTimeValues] = useState({ h1: 0, h2: 0, m1: 0, m2: 0, s1: 0, s2: 0 });

  // Parse duration string (HH:MM:SS) to readable format
  const parseDuration = (durationStr) => {
    if (!durationStr || durationStr === "00:00:00") {
      return { h1: 0, h2: 0, m1: 0, m2: 0, s1: 0, s2: 0 };
    }
    
    const parts = durationStr.split(':');
    const hours = parts[0] || '00';
    const minutes = parts[1] || '00';
    const seconds = parts[2] || '00';
    
    return {
      h1: Math.floor(parseInt(hours) / 10) || 0,
      h2: parseInt(hours) % 10 || 0,
      m1: Math.floor(parseInt(minutes) / 10) || 0,
      m2: parseInt(minutes) % 10 || 0,
      s1: Math.floor(parseInt(seconds) / 10) || 0,
      s2: parseInt(seconds) % 10 || 0,
    };
  };

  // Format time values back to HH:MM:SS string
  const formatDuration = (values) => {
    const hours = `${values.h1}${values.h2}`.padStart(2, '0');
    const minutes = `${values.m1}${values.m2}`.padStart(2, '0');
    const seconds = `${values.s1}${values.s2}`.padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  // Convert duration to readable format for display
  const formatDurationForDisplay = (durationStr) => {
    if (!durationStr || durationStr === "00:00:00") {
      return "Not specified";
    }
    const [hours, minutes] = durationStr.split(":");
    if (hours === "00" && minutes === "00") {
      return "Not specified";
    }
    return `${parseInt(hours)} Hours ${parseInt(minutes)} Minutes`;
  };

  const handleEditStart = () => {
    setTimeValues(parseDuration(value));
    setIsEditing(true);
  };

  const handleInputChange = (field, inputValue) => {
    const numValue = parseInt(inputValue) || 0;
    let validValue = Math.max(0, Math.min(9, numValue));
    
    // Apply constraints for hours, minutes, seconds
    if (field === 'h1' && numValue > 2) validValue = 2;
    if (field === 'h2' && timeValues.h1 === 2 && numValue > 3) validValue = 3;
    if ((field === 'm1' || field === 's1') && numValue > 5) validValue = 5;

    setTimeValues(prev => ({ ...prev, [field]: validValue }));
  };

  const handleKeyDown = (e, field, nextField) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
      return;
    }
    
    if (e.key === 'Escape') {
      e.preventDefault();
      setIsEditing(false);
      return;
    }

    if ((e.key === 'ArrowRight' || e.key === 'Tab') && nextField) {
      e.preventDefault();
      const nextInput = document.querySelector(`[data-field="${nextField}"]`);
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    }
  };

  const handleSave = () => {
    const formattedDuration = formatDuration(timeValues);
    onChange(formattedDuration);
    setIsEditing(false);
  };

  const handleBlur = (e) => {
    // Only save if focus is not moving to another time input within the same editor
    setTimeout(() => {
      const activeElement = document.activeElement;
      const isStillInTimeEditor = activeElement && activeElement.closest('.duration-editor');
      
      if (!isStillInTimeEditor) {
        handleSave();
      }
    }, 100);
  };

  if (isEditing) {
    return (
      <div className="duration-editor">
        <input
          type="number"
          min="0" max="2"
          value={timeValues.h1}
          onChange={(e) => handleInputChange('h1', e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, 'h1', 'h2')}
          onBlur={handleBlur}
          className="time-box"
          data-field="h1"
          autoFocus
        />
        <input
          type="number"
          min="0" max="9"
          value={timeValues.h2}
          onChange={(e) => handleInputChange('h2', e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, 'h2', 'm1')}
          onBlur={handleBlur}
          className="time-box"
          data-field="h2"
        />
        <span>:</span>
        <input
          type="number"
          min="0" max="5"
          value={timeValues.m1}
          onChange={(e) => handleInputChange('m1', e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, 'm1', 'm2')}
          onBlur={handleBlur}
          className="time-box"
          data-field="m1"
        />
        <input
          type="number"
          min="0" max="9"
          value={timeValues.m2}
          onChange={(e) => handleInputChange('m2', e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, 'm2', 's1')}
          onBlur={handleBlur}
          className="time-box"
          data-field="m2"
        />
        <span>:</span>
        <input
          type="number"
          min="0" max="5"
          value={timeValues.s1}
          onChange={(e) => handleInputChange('s1', e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, 's1', 's2')}
          onBlur={handleBlur}
          className="time-box"
          data-field="s1"
        />
        <input
          type="number"
          min="0" max="9"
          value={timeValues.s2}
          onChange={(e) => handleInputChange('s2', e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, 's2', null)}
          onBlur={handleBlur}
          className="time-box"
          data-field="s2"
        />
      </div>
    );
  }

  return (
    <span
      className={`duration-display ${className || ''}`}
      onClick={handleEditStart}
      title="Click to edit duration"
    >
      {formatDurationForDisplay(value) || placeholder}
      <Edit3 size={12} className="edit-icon" />
    </span>
  );
};
// ✅ UPDATED: Enhanced error tooltip with better positioning
const InlineEditableNumber = ({
  value,
  onChange,
  className,
  min = 1,
  max = 100,
  examData = null,
  questionId = null,
  isTotalMarks = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [error, setError] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState('default'); // ✅ NEW: Track tooltip position
  const inputRef = useRef(null);
  const containerRef = useRef(null); // ✅ NEW: Container reference

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // ✅ NEW: Check tooltip positioning when error appears
  useEffect(() => {
    if (error && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      
      // If tooltip would go off the right edge, position it differently
      if (rect.right > viewportWidth - 200) {
        setTooltipPosition('tooltip-right');
      } else {
        setTooltipPosition('default');
      }
    }
  }, [error]);

  const handleSave = () => {
    const numValue = parseInt(tempValue, 10);
    
    if (isNaN(numValue) || numValue < min || numValue > max) {
      setError(`Value must be between ${min} and ${max}`);
      return;
    }

    if (isTotalMarks && examData) {
      const validation = validateTotalExamMarks(examData, numValue);
      if (!validation.isValid) {
        setError(validation.error);
        return;
      }
    }

    if (examData && questionId && !isTotalMarks) {
      const validation = validateQuestionMarks(examData, questionId, numValue);
      if (!validation.isValid) {
        setError(validation.error);
        return;
      }
    }

    setError(null);
    onChange(numValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTempValue(value);
    setError(null);
    setIsEditing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setTempValue(newValue);
    
    const numValue = parseInt(newValue, 10);
    if (!isNaN(numValue)) {
      if (isTotalMarks && examData) {
        const validation = validateTotalExamMarks(examData, numValue);
        if (!validation.isValid) {
          setError(validation.error);
        } else {
          setError(null);
        }
      } else if (examData && questionId) {
        const validation = validateQuestionMarks(examData, questionId, numValue);
        if (!validation.isValid) {
          setError(validation.error);
        } else {
          setError(null);
        }
      }
    }
  };

  if (isEditing) {
    return (
      <div ref={containerRef} className="inline-number-editor-container"> {/* ✅ UPDATED: Add ref */}
        <input
          ref={inputRef}
          type="number"
          value={tempValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          onBlur={handleSave}
          className={`inline-number-editor ${className} ${error ? 'error' : ''}`}
          min={min}
          max={max}
        />
        {error && (
          <div className={`marks-error-tooltip ${tooltipPosition !== 'default' ? tooltipPosition : ''}`}> {/* ✅ UPDATED: Add conditional class */}
            <AlertTriangle size={12} />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <span
      className={`inline-editable-number ${className}`}
      onClick={() => setIsEditing(true)}
      title="Click to edit marks"
    >
      {value}
    </span>
  );
};
const SectionTitleEditor = ({
  section,
  isEditing,
  onStartEdit,
  onSave,
  onCancel,
  onMetadataUpdate,
  isSelected,
  // ✅ NEW: Add exam context for better API handling
  examData = null,
  examId = null,
  fetchExamDetails = null,
}) => {
  const [titleContent, setTitleContent] = useState(null);
  const editorRef = useRef(null);

  // Initialize title content when editing starts
  useEffect(() => {
    if (isEditing && section) {
      // Check if section has rich content structure for title
      if (section.titleRichContent) {
        setTitleContent(section.titleRichContent);
      } else {
        // Convert plain title to rich content structure
        setTitleContent({
          type: "rich-text",
          html: `<p>${section.title || "New Section"}</p>`,
          plainText: section.title || "New Section",
        });
      }
    }
  }, [isEditing, section]);

  const handleSave = async () => {
    if (!titleContent) return;

    try {
      // Extract plain text for the title field
      const plainTitle =
        titleContent.plainText ||
        titleContent.html?.replace(/<[^>]*>/g, "") ||
        "New Section";

      // Update section with both plain title and rich content
      const updatedSection = {
        ...section,
        title: plainTitle,
        titleRichContent: titleContent,
      };

      // Call the save handler - this will handle the API calls properly
      await onSave(updatedSection);

      // ✅ REMOVED: Don't call onMetadataUpdate separately since onSave handles it
      // The parent component (Canvas) handles the full API workflow
    } catch (error) {
      console.error("Error saving section title:", error);
      // Don't reset titleContent here - let the parent handle the error state
    }
  };

  const handleCancel = () => {
    setTitleContent(null);
    onCancel();
  };

  const handleKeyDown = (e) => {
    // Handle keyboard shortcuts for quick save/cancel
    if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
    // For rich text editor, we'll rely on the toolbar save button
    // rather than Enter key to avoid conflicts with rich text editing
  };

  const handleTitleContentChange = (content) => {
    setTitleContent(content);
  };

  // Render editing mode with RichTextEditor
  if (isEditing) {
    return (
      <div className="section-title-rich-editor" onKeyDown={handleKeyDown}>
        <div className="rich-title-editor-container">
          <EnhancedRichTextEditor
            ref={editorRef}
            value={titleContent}
            onChange={handleTitleContentChange}
            placeholder="Enter section title..."
            showMathTools={true}
            isEditMode={true}
            maxHeight="150px"
            showToolbar={true}
            className="section-title-rich-editor-field"
            // ✅ NEW: Don't pass onMetadataChange for real-time updates
            // We'll handle metadata updates only on save to avoid API spam
          />
        </div>

        <div className="section-title-actions">
          <button
            onClick={handleSave}
            className="title-save-btn"
            title="Save section title"
            type="button"
          >
            <Check size={14} />
            <span>Save</span>
          </button>
          <button
            onClick={handleCancel}
            className="title-cancel-btn"
            title="Cancel editing"
            type="button"
          >
            <X size={14} />
            <span>Cancel</span>
          </button>
        </div>

        <style jsx>{`
          .section-title-rich-editor {
            width: 100%;
            margin-bottom: 0.75rem;
          }

          .rich-title-editor-container {
            margin-bottom: 0.75rem;
            background: #ffffff;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            border: 2px solid #3b82f6;
          }

          .section-title-rich-editor-field {
            border: none !important;
            border-radius: 8px !important;
          }

          .section-title-actions {
            display: flex;
            gap: 0.5rem;
            justify-content: flex-end;
            padding: 0.5rem;
            background: #f8fafc;
            border-radius: 6px;
            border: 1px solid #e2e8f0;
          }

          .title-save-btn,
          .title-cancel-btn {
            display: flex;
            align-items: center;
            gap: 0.375rem;
            padding: 0.5rem 1rem;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 0.875rem;
            font-weight: 500;
            transition: all 0.2s ease;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }

          .title-save-btn {
            background: #10b981;
            color: white;
          }

          .title-save-btn:hover {
            background: #059669;
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
          }

          .title-cancel-btn {
            background: #6b7280;
            color: white;
          }

          .title-cancel-btn:hover {
            background: #4b5563;
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(107, 114, 128, 0.3);
          }

          .title-save-btn:active,
          .title-cancel-btn:active {
            transform: translateY(0);
          }

          /* Responsive design */
          @media (max-width: 768px) {
            .section-title-actions {
              flex-direction: column;
              gap: 0.25rem;
            }

            .title-save-btn,
            .title-cancel-btn {
              justify-content: center;
              padding: 0.625rem 1rem;
            }
          }

          @media (max-width: 480px) {
            .section-title-actions {
              padding: 0.375rem;
            }

            .title-save-btn,
            .title-cancel-btn {
              font-size: 0.8rem;
              padding: 0.5rem 0.875rem;
            }
          }
        `}</style>
      </div>
    );
  }

  // Render display mode - clickable title with RichContentRenderer
  return (
    <h4
      className={`section-title-display ${isSelected ? "selected" : ""}`}
      onClick={onStartEdit}
      title="Click to edit section title"
    >
      {/* ✅ NEW: Use RichContentRenderer to display rich content properly */}
      <div className="section-title-content">
        {section.titleRichContent ? (
          <RichContentRenderer
            content={section.titleRichContent}
            isEditMode={false}
          />
        ) : (
          <span className="section-title-plain">{section.title}</span>
        )}
      </div>

      {isSelected && (
        <span className="selection-indicator">Section Selected</span>
      )}

      <style jsx>{`
        .section-title-display {
          font-size: 0.95rem;
          font-weight: 700;
          margin: 0;
          display: flex;
          align-items: center;
          color: #1e293b;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: "Times New Roman", serif;
          padding: 0.375rem 0.5rem;
          border-radius: 4px;
          border: 1px solid transparent;
        }

        .section-title-display:hover {
          color: #3b82f6;
          background: #f8fafc;
          border-color: #e2e8f0;
        }

        .section-title-display.selected {
          background: #eff6ff;
          border-color: #3b82f6;
          color: #1e40af;
        }

        .section-title-content {
          flex: 1;
          min-width: 0;
          word-wrap: break-word;
        }

        /* ✅ NEW: Styles for rich content in section titles */
        .section-title-content :global(p) {
          margin: 0;
          padding: 0;
          line-height: 1.3;
          display: inline;
        }

        .section-title-content :global(strong) {
          font-weight: 800;
        }

        .section-title-content :global(em) {
          font-style: italic;
        }

        .section-title-content :global(u) {
          text-decoration: underline;
        }

        /* ✅ NEW: Math elements in section titles */
        .section-title-content :global(.math-inline) {
          display: inline !important;
          vertical-align: middle !important;
          margin: 0 2px !important;
          background: transparent !important;
          border: none !important;
          padding: 0 !important;
        }

        .section-title-content :global(.math-inline math-field) {
          display: inline !important;
          font-size: inherit !important;
          font-family: inherit !important;
          background: transparent !important;
          border: none !important;
          padding: 0 !important;
          margin: 0 !important;
          vertical-align: middle !important;
        }

        /* ✅ NEW: Ensure delete buttons don't show in display mode */
        .section-title-content :global(.math-delete-btn) {
          display: none !important;
        }

        .section-title-plain {
          flex: 1;
          min-width: 0;
          word-wrap: break-word;
        }

        .selection-indicator {
          background: #3b82f6;
          color: white;
          font-size: 10px;
          padding: 2px 8px;
          border-radius: 12px;
          margin-left: 12px;
          font-weight: 500;
          white-space: nowrap;
          flex-shrink: 0;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .section-title-display {
            font-size: 0.9rem;
            padding: 0.5rem;
          }

          .selection-indicator {
            font-size: 9px;
            padding: 1px 6px;
            margin-left: 8px;
          }
        }

        @media (max-width: 480px) {
          .section-title-display {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.25rem;
          }

          .selection-indicator {
            margin-left: 0;
            align-self: flex-start;
          }
        }
      `}</style>
    </h4>
  );
};


const Canvas = () => {
  const context = useContext(ExamContext);
  const [editingQuestion, setEditingQuestion] = useState(null);

  const [sectionTitleValue, setSectionTitleValue] = useState("");
  const [sectionAlignment, setSectionAlignment] = useState("left");
  const [sectionHasInstructions, setSectionHasInstructions] = useState(false);
  const [sectionInstructions, setSectionInstructions] = useState({
    type: "rich-text",
    html: "<p>Enter section instructions here...</p>",
    plainText: "Enter section instructions here...",
  });
  const [animatingElements, setAnimatingElements] = useState(new Set());
  const canvasRef = useRef(null);
  const [editingSectionTitle, setEditingSectionTitle] = useState(null);
  // Update editing state in context when local editing state changes
  useEffect(() => {
    if (context && context.setEditingMode) {
      context.setEditingMode(
        editingQuestion !== null || editingSectionTitle !== null
      );
    }
  }, [editingQuestion, editingSectionTitle, context]);

  if (!context) return null;

  const {
    exam,
    updateExam,
    selectedNode,
    selectNode,
    setEditingMode,
    fetchExamDetails,
  } = context;
  const currentPageSize = PAPER_SIZES[exam.pagination.paperSize];

  // ✅ NEW: Metadata management functions
  // ✅ FIXED: Enhanced metadata update that always uses API
  const handleSectionMetadataUpdate = async (sectionId, instructions) => {
    try {
      // Update the section via API
      const sectionData = {
        name:
          exam.pages[0].sections.find((s) => s.id === sectionId)?.title ||
          "Section",
        description:
          instructions.plainText ||
          instructions.html?.replace(/<[^>]*>/g, "") ||
          "",
        serialNumber:
          exam.pages[0].sections.find((s) => s.id === sectionId)
            ?.serialNumber || 1,
      };

      const response = await updateTemplateSection(sectionId, sectionData);

      if (response.status === "success" || response.status === "200") {
        // Fetch fresh data after successful update
        await fetchExamDetails();
        console.log("Section metadata updated successfully");
      } else {
        throw new Error(response.message || "Failed to update section");
      }
    } catch (error) {
      console.error("Error updating section metadata:", error);
      alert("Failed to update section. Please try again.");
    }
  };

  const handleSectionTitleUpdate = async (updatedSection) => {
    try {
      console.log("Updating section title:", updatedSection);

      // ✅ CRITICAL: Use the same API pattern as TopToolbar.js for updating sections
      // Looking at your code, updateTemplateSection expects just the section data
      const sectionApiData = {
        name: updatedSection.title,
        sectionId: updatedSection.id,
        id: updatedSection.id,
        description:
          updatedSection.instructions?.plainText ||
          updatedSection.instructions?.html?.replace(/<[^>]*>/g, "") ||
          "Enter section instructions here...",
        serialNumber: updatedSection.serialNumber || 1,
        organizationId: exam.organizationId,
        templateId: exam.id,
        siteId: exam.siteId,
        regionId: exam.regionId,


      };

      console.log("Section API data being sent:", sectionApiData);

      // ✅ FIXED: Use the correct API call pattern from your existing code
      const response = await updateTemplateSection(   sectionApiData,
        updatedSection.id
     
      );

      if (response.status === "success" || response.status === "200") {
        console.log("Section title updated successfully via API");

        // Step 2: Create updated exam data with the new section
        const updatedExamData = {
          ...exam,
          // ✅ CRITICAL: Preserve globalEquations and richTextMetadata like in TopToolbar.js
          globalEquations: exam.globalEquations || {},
          richTextMetadata: exam.richTextMetadata || {},
          pages: exam.pages.map((page) => ({
            ...page,
            sections: page.sections.map((section) =>
              section.id === updatedSection.id ? updatedSection : section
            ),
          })),
        };

        // ✅ CRITICAL: Update richTextMetadata for the section if it doesn't exist
        if (!updatedExamData.richTextMetadata[updatedSection.id]) {
          updatedExamData.richTextMetadata[updatedSection.id] = {
            instructions: updatedSection.instructions || null,
            questions:
              updatedSection.questions?.map((question) => ({
                questionId: question.id,
                configuration: question.richContent,
              })) || [],
          };
        }

        // Step 3: Use the exact same utility function pattern as TopToolbar.js
        const metadataResult = await updateExaminationTemplateWithMetadata(
          updatedExamData,
          exam.id,
          fetchExamDetails
        );

        if (metadataResult.success) {
          console.log("Section title and metadata updated successfully");
          setEditingSectionTitle(null);
        } else {
          console.error(
            "Failed to update metadata after section title change:",
            metadataResult.error
          );
          // Still fetch fresh data even if metadata update failed
          await fetchExamDetails();
          setEditingSectionTitle(null);
        }
      } else {
        console.error("Failed to update section via API:", response);
        throw new Error(response.message || "Failed to update section title");
      }
    } catch (error) {
      console.error("Error updating section title:", error);
      alert("Failed to update section title. Please try again.");
    }
  };
  const handleQuestionMetadataUpdate = async (
    questionContext,
    configuration
  ) => {
    const { sectionId, questionId } = questionContext;

    try {
      // Find the question to get current data
      let currentQuestion = null;
      exam.pages.forEach((page) => {
        page.sections.forEach((section) => {
          if (section.id === sectionId) {
            currentQuestion = section.questions.find(
              (q) => q.id === questionId
            );
          }
        });
      });

      if (!currentQuestion) {
        throw new Error("Question not found");
      }

      // Step 1: Update the question via API
      const questionApiData = {
        question:
          configuration.plainText ||
          configuration.html?.replace(/<[^>]*>/g, "") ||
          "",
        score: currentQuestion.marks || 1,
        serialNumber: currentQuestion.serialNumber || 1,
        options:
          currentQuestion.type === "with-options"
            ? (currentQuestion.options || []).map(
                (opt) => opt.richContent?.plainText || opt.content || ""
              )
            : [],
        rightOptions: currentQuestion.correctAnswers || [],
        isMultiSelect: currentQuestion.isMultiSelect || "N",
      };

      const questionResponse = await updateQuestion(
        questionId,
        questionApiData
      );

      if (
        questionResponse.status === "success" ||
        questionResponse.status === "200"
      ) {
        console.log("Question metadata updated successfully");

        // Step 2: Update the exam template with updated metadata including equations
        const updatedExamData = {
          ...exam,
          pages: exam.pages.map((page) => ({
            ...page,
            sections: page.sections.map((section) =>
              section.id === sectionId
                ? {
                    ...section,
                    questions: section.questions.map((q) =>
                      q.id === questionId
                        ? { ...q, richContent: configuration }
                        : q
                    ),
                  }
                : section
            ),
          })),
        };

        // ✅ UPDATED: Generate metadata with equations
        const updatedMetadata = generateMetadataFromExam(updatedExamData);

        // Prepare template data
        const templateApiData = {
          ...exam,
          metaData: updatedMetadata,
        };

        const templateResponse = await updateExaminationTemplate(
          exam.id,
          templateApiData
        );

        if (
          templateResponse.status === "success" ||
          templateResponse.status === "200"
        ) {
          console.log("Exam template updated after question metadata change");
          // Fetch fresh data from API
          await fetchExamDetails();
        } else {
          console.error("Failed to update exam template:", templateResponse);
        }
      } else {
        throw new Error(
          questionResponse.message || "Failed to update question"
        );
      }
    } catch (error) {
      console.error("Error updating question metadata:", error);
    }
  };

  // Calculate total marks
  const calculateTotalMarks = () => {
    return exam.pages.reduce(
      (total, page) =>
        total +
        page.sections.reduce(
          (sectionTotal, section) =>
            sectionTotal +
            section.questions.reduce(
              (questionTotal, question) =>
                questionTotal + (question.marks || 1),
              0
            ),
          0
        ),
      0
    );
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question.id);
    selectNode("question", question.id);
  };

  const handleUpdateQuestion = (updatedQuestion) => {
    updateExam((exam) => ({
      ...exam,
      pages: exam.pages.map((page) => ({
        ...page,
        sections: page.sections.map((section) => ({
          ...section,
          questions: section.questions.map((q) =>
            q.id === updatedQuestion.id ? updatedQuestion : q
          ),
        })),
      })),
    }));
  };

  async function handleSaveQuestion() {
    setEditingQuestion(null);
  }

  const handleCancelEdit = () => {
    setEditingQuestion(null);
  };

  const handleSectionClick = (section, event) => {
    event.stopPropagation();
    if (
      selectedNode &&
      selectedNode.type === "section" &&
      selectedNode.id === section.id
    ) {
      return; // Already selected
    }
    console.log("Section clicked:", section);
    selectNode("section", section.id);
  };

  const handleSectionTitleClick = (section, event) => {
    event.stopPropagation();
    if (editingSectionTitle !== section.id) {
      setEditingSectionTitle(section.id);
      setSectionTitleValue(section.title);
    }
  };

  const handleSectionTitleSave = (sectionId) => {
    updateExam((exam) => ({
      ...exam,
      pages: exam.pages.map((page) => ({
        ...page,
        sections: page.sections.map((section) =>
          section.id === sectionId
            ? { ...section, title: sectionTitleValue }
            : section
        ),
      })),
    }));
    setEditingSectionTitle(null);
    setSectionTitleValue("");
  };

  const handleSectionTitleCancel = () => {
    setEditingSectionTitle(null);
    setSectionTitleValue("");
  };

  const handleSectionTitleKeyPress = (event, sectionId) => {
    if (event.key === "Enter") {
      handleSectionTitleSave(sectionId);
    } else if (event.key === "Escape") {
      handleSectionTitleCancel();
    }
  };

  // Replace the existing handleMetadataUpdate function with this updated version
  // Replace the existing handleMetadataUpdate function with this updated version
  const handleMetadataUpdate = async (field, value) => {
    try {
      let updatedExamData = { ...exam };

      // Special handling for instructions field to support rich text
      if (field === "instructions") {
        if (typeof value === "object" && value.plainText) {
          // Update the metadata with rich content structure
          updatedExamData.metadata = {
            ...updatedExamData.metadata,
            instructions: value.plainText,
            instructionsRichContent: value,
          };
        } else if (typeof value === "string") {
          updatedExamData.metadata = {
            ...updatedExamData.metadata,
            instructions: value,
          };
        }
      } else {
        // Handle other metadata fields
        if (field === "timeAllowed") {
          updatedExamData.duration = value;
          updatedExamData.metadata = {
            ...updatedExamData.metadata,
            timeAllowed: value,
          };
        } else if (field === "totalMarks") {
          updatedExamData.score = value;
          updatedExamData.metadata = {
            ...updatedExamData.metadata,
            totalMarks: value,
          };
        } else {
          updatedExamData.metadata = {
            ...updatedExamData.metadata,
            [field]: value,
          };
        }
      }

      // Generate proper metadata structure using the utility function
      const updatedMetadata = generateMetadataFromExam(updatedExamData);

      // Prepare API payload
      const examApiData = {
        ...exam,
        metadata: updatedMetadata,
      };

      // Add direct field updates for API compatibility
      if (field === "timeAllowed") {
        examApiData.duration = value;
      }
      if (field === "totalMarks") {
        examApiData.score = value;
      }
      if (field === "instructions") {
        examApiData.instructions =
          typeof value === "object" ? value.plainText : value;
      } else {
        examApiData[field] = value;
      }

      // Clean up the payload
      delete examApiData.pages;
      delete examApiData.pagination;
      delete examApiData.customEquationTemplates;
      delete examApiData.richTextMetadata;
      delete examApiData.globalEquations;

      const response = await updateExaminationTemplate(exam.id, examApiData);

      if (response.status === "success" || response.status === "200") {
        // For instructions, don't fetch details to maintain editing state
        await fetchExamDetails();
        console.log("Exam metadata updated successfully");
      } else {
        throw new Error(response.message || "Failed to update exam metadata");
      }
    } catch (error) {
      console.error("Error updating exam metadata:", error);
      alert("Failed to update exam metadata. Please try again.");
    }
  };

  // Handle question marks update
  // Handle question marks update with validation
  const handleQuestionMarksUpdate = async (questionId, newMarks) => {
    try {
      // ✅ NEW: Validate marks before API call
      const validation = validateQuestionMarks(exam, questionId, newMarks);
      if (!validation.isValid) {
        alert(validation.error);
        return;
      }

      // Find the question to get current data
      let currentQuestion = null;
      let sectionId = null;

      exam.pages.forEach((page) => {
        page.sections.forEach((section) => {
          const question = section.questions.find((q) => q.id === questionId);
          if (question) {
            currentQuestion = question;
            sectionId = section.id;
          }
        });
      });

      if (!currentQuestion || !sectionId) {
        throw new Error("Question not found");
      }

      // Step 1: Update the question via API
      const questionApiData = {
        question:
          currentQuestion.richContent?.plainText ||
          currentQuestion.content ||
          "",
        score: newMarks,
        serialNumber: currentQuestion.serialNumber || 1,
        options:
          currentQuestion.type === "with-options"
            ? (currentQuestion.options || []).map(
                (opt) => opt.richContent?.plainText || opt.content || ""
              )
            : [],
        rightOptions: currentQuestion.correctAnswers || [],
        isMultiSelect: currentQuestion.isMultiSelect || "N",
      };

      const questionResponse = await updateQuestion(
        questionId,
        questionApiData
      );

      if (
        questionResponse.status === "success" ||
        questionResponse.status === "200"
      ) {
        console.log("Question marks updated successfully");

        // Step 2: Update the exam template with updated metadata
        const updatedExamData = {
          ...exam,
          pages: exam.pages.map((page) => ({
            ...page,
            sections: page.sections.map((section) => ({
              ...section,
              questions: section.questions.map((q) =>
                q.id === questionId ? { ...q, marks: newMarks } : q
              ),
            })),
          })),
        };

        // Calculate new total marks
        const newTotalMarks = updatedExamData.pages.reduce(
          (total, page) =>
            total +
            page.sections.reduce(
              (sectionTotal, section) =>
                sectionTotal +
                section.questions.reduce(
                  (questionTotal, question) =>
                    questionTotal + (question.marks || 1),
                  0
                ),
              0
            ),
          0
        );

        // Update metadata with new total marks
        updatedExamData.metadata = {
          ...updatedExamData.metadata,
          totalMarks: newTotalMarks,
        };

        // Generate updated metadata
        const updatedMetadata = generateMetadataFromExam(updatedExamData);

        // Prepare template data
        const templateApiData = {
          id: exam.id,
          name: exam.metadata?.heading || exam.name || "Question Paper",
          description: exam.metadata?.subheading || exam.description || "",
          instructions: exam.metadata?.instructions || "",
          score: exam.score, // Keep original total marks, don't auto-update
          examType: exam.metadata?.examType || "SUBJECTIVE",
          metadata: updatedMetadata,
        };

        const templateResponse = await updateExaminationTemplate(
          exam.id,
          templateApiData
        );

        if (
          templateResponse.status === "success" ||
          templateResponse.status === "200"
        ) {
          console.log("Exam template updated after marks change");
          // Fetch fresh data from API
          await fetchExamDetails();
        } else {
          console.error("Failed to update exam template:", templateResponse);
        }
      } else {
        throw new Error(
          questionResponse.message || "Failed to update question marks"
        );
      }
    } catch (error) {
      console.error("Error updating question marks:", error);
      alert("Failed to update question marks. Please try again.");
    }
  };

  const isSelected = (type, id) => {
    return selectedNode && selectedNode.type === type && selectedNode.id === id;
  };

  const renderQuestion = (question, questionIndex, sectionIndex, pageIndex) => {
    const isEditing = editingQuestion === question.id;
    const isQuestionSelected = isSelected("question", question.id);
    const isNewQuestion = question.isNew;
    console.log("Rendering question:", question);
    // ✅ NEW: Find section ID for metadata context
    const currentSection = exam.pages[pageIndex]?.sections[sectionIndex];
    const metadataContext = {
      sectionId: currentSection?.id,
      questionId: question.id,
    };

    if (isEditing) {
      return (
        <div key={generateId()} className="question-item editing">
          <div className="question-number">{questionIndex + 1}.</div>
          <div className="question-content-editing">
            <div className="editing-container">
              <InlineQuestionEditor
                question={question}
                onUpdate={handleUpdateQuestion}
                onSave={handleSaveQuestion}
                onCancel={handleCancelEdit}
                // onMetadataChange={handleQuestionMetadataUpdate}
                metadataContext={metadataContext}
                // ✅ NEW: Add these props
                examData={exam}
                examId={exam.id}
                fetchExamDetails={fetchExamDetails}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        key={generateId()}
        className={`question-item ${
          isQuestionSelected ? "question-selected" : ""
        } ${animatingElements.has(question.id) ? "animate-in" : ""} ${
          isNewQuestion ? "new-question" : ""
        }`}
        onClick={() => handleEditQuestion(question)}
      >
        <div className="question-number">{questionIndex + 1}.</div>
        <div className="question-content">
          <div className="question-text">
            <RichContentRenderer
              content={
                question.richContent || {
                  type: "text",
                  content: question.content,
                }
              }
            />
          </div>

          {question.type === "with-options" &&
            question.options &&
            question.options.length > 0 && (
              <div
                className={`question-options ${
                  question.optionsLayout || "vertical"
                }`}
              >
                {question.options.map((option, optionIndex) => (
                  <div key={ optionIndex} className="option-item">
                    <span className="option-label">
                      ({String.fromCharCode(97 + optionIndex)})
                    </span>
                    <span className="option-text">
                      <RichContentRenderer
                        content={
                          option.richContent || {
                            type: "text",
                            content: option.content || option,
                          }
                        }
                      />
                    </span>
                  </div>
                ))}
              </div>
            )}
          <div className="question-marks">
            [
            <InlineEditableNumber
              value={question.marks || 1}
              onChange={(newMarks) =>
                handleQuestionMarksUpdate(question.id, newMarks)
              }
              className="marks-input"
              examData={exam} // ✅ NEW: Pass exam data
              questionId={question.id} // ✅ NEW: Pass question ID
            />{" "}
            mark{(question.marks || 1) !== 1 ? "s" : ""}]
          </div>
        </div>
      </div>
    );
  };

  const renderSection = (section, sectionIndex, pageIndex) => {
    const isSectionSelected = isSelected("section", section.id);
    const isEditingTitle = editingSectionTitle === section.id;
    const hasQuestions = section.questions && section.questions.length > 0;
    const hasInstructions =
      section.hasInstructions &&
      section.instructions &&
      section.instructions.plainText &&
      section.instructions.plainText.trim() !== "" &&
      section.instructions.plainText !== "Enter section instructions here...";

    // Check if any question in this section is being edited
    const hasEditingQuestion = section.questions.some(
      (q) => editingQuestion === q.id
    );

    const handleSectionInstructionsToggle = (sectionId, hasInstructions) => {
      updateExam((exam) => ({
        ...exam,
        pages: exam.pages.map((page) => ({
          ...page,
          sections: page.sections.map((section) =>
            section.id === sectionId
              ? {
                  ...section,
                  hasInstructions,
                  instructions: hasInstructions
                    ? section.instructions || {
                        type: "rich-text",
                        html: "<p>Enter section instructions here...</p>",
                        plainText: "Enter section instructions here...",
                      }
                    : section.instructions,
                }
              : section
          ),
        })),
      }));
    };

    const handleSectionInstructionsChange = (sectionId, newInstructions) => {
      updateExam((exam) => ({
        ...exam,
        pages: exam.pages.map((page) => ({
          ...page,
          sections: page.sections.map((section) =>
            section.id === sectionId
              ? { ...section, instructions: newInstructions }
              : section
          ),
        })),
      }));

      // ✅ FIXED: Don't update metadata during typing - handled on blur/save instead
    };

    return (
      <div
        key={generateId()}
        className={`section ${isSectionSelected ? "section-selected" : ""} ${
          !hasQuestions ? "empty-section" : ""
        } ${animatingElements.has(section.id) ? "animate-in" : ""} ${
          hasEditingQuestion ? "has-editing-question" : ""
        }`}
        onClick={(e) => handleSectionClick(section, e)}
      >
        <div
          className="section-header"
          style={{ textAlign: section.titleAlignment }}
        >
          <SectionTitleEditor
            section={section}
            isEditing={editingSectionTitle === section.id}
            onStartEdit={(e) => {
              e.stopPropagation();
              setEditingSectionTitle(section.id);
            }}
            onSave={handleSectionTitleUpdate}
            onCancel={() => setEditingSectionTitle(null)}
            isSelected={isSectionSelected}
            // ✅ NEW: Pass exam context like other components
            examData={exam}
            examId={exam.id}
            fetchExamDetails={fetchExamDetails}
          />
        </div>

        {/* Section Instructions */}
        {/* {(hasInstructions || isSectionSelected) && (
          <div className="section-instructions">
            {isSectionSelected ? (
              <div className="section-instructions-editor">
                <div className="instructions-header">
                  <label>Section Instructions:</label>
                  <div className="instructions-controls">
                    <label className="checkbox-container">
                      <input
                        type="checkbox"
                        checked={section.hasInstructions || false}
                        onChange={(e) =>
                          handleSectionInstructionsToggle(
                            section.id,
                            e.target.checked
                          )
                        }
                      />
                      <span className="checkmark"></span>
                      Show instructions
                    </label>
                  </div>
                </div>
                {section.hasInstructions && (
                  <div className="instructions-content">
                    <EnhancedRichTextEditor
                      value={
                        section.instructions || {
                          type: "rich-text",
                          html: "<p>Enter section instructions here...</p>",
                          plainText: "Enter section instructions here...",
                        }
                      }
                      onChange={(content) =>
                        handleSectionInstructionsChange(section.id, content)
                      }
                      placeholder="Enter instructions for this section..."
                      showMathTools={true}
                      isEditMode={true}
                      onMetadataChange={handleSectionMetadataUpdate}
                      metadataContext={section.id}
                    />
                  </div>
                )}
              </div>
            ) : hasInstructions ? (
              <div className="section-instructions-display">
                <div className="instructions-label">Instructions:</div>
                <div className="instructions-text">
                  <RichContentRenderer content={section.instructions} />
                </div>
              </div>
            ) : null}
          </div>
        )} */}

        <div className="section-content">
          {hasQuestions ? (
            section.questions.map((question, questionIndex) => {
              let questionOffset = 0;
              for (let i = 0; i < pageIndex; i++) {
                for (let j = 0; j < organizedPages[i].sections.length; j++) {
                  questionOffset +=
                    organizedPages[i].sections[j].questions.length;
                }
              }
              for (let k = 0; k < sectionIndex; k++) {
                questionOffset +=
                  organizedPages[pageIndex].sections[k].questions.length;
              }

              return renderQuestion(
                question,
                questionOffset + questionIndex,
                sectionIndex,
                pageIndex
              );
            })
          ) : (
            <div
              className={`empty-section-placeholder ${
                isSectionSelected ? "selected" : ""
              }`}
            >
              <div className="empty-section-content">
                <div className="empty-section-icon">
                  <Plus size={24} />
                </div>
                <div className="empty-section-text">
                  <h5>No questions in this section</h5>
                  <p>
                    Click "Add Question" to add your first question to this
                    section.
                  </p>
                </div>
                <button
                  className="add-question-placeholder-btn"
                  onClick={(e) => {
                    e.stopPropagation();

                    // ✅ USE SAME LOGIC AS TOPBAR: Add question to section
                    const addQuestionToSectionInline = async () => {
                      try {
                        console.log("Adding question to section:", section);

                        // ✅ FIXED: Create question with proper structure
                        const newQuestion = createNewQuestion(exam, {
                          type: "section",
                          id: section.id,
                        });

                        // ✅ FIXED: Ensure the question has proper rich content structure
                        // if (!newQuestion.richContent) {
                        //   newQuestion.richContent = {
                        //     type: "rich-text",
                        //     html: "<p>Enter your question here...</p>",
                        //     plainText: "Enter your question here...",
                        //   };
                        // }

                        // Step 1: Add question via API
                        const response = await addQuestion(newQuestion);

                        if (response.status === "success") {
                          // Step 2: Update local exam state with the new question
                          const questionId =
                            response.data?.id ||
                            response.response?.id ||
                            generateId();
                          const finalQuestion = {
                            ...newQuestion,
                            id: questionId,
                          };

                          const updatedExamData = {
                            ...exam,
                            // ✅ CRITICAL: Preserve globalEquations and richTextMetadata
                            globalEquations: exam.globalEquations || {},
                            richTextMetadata: exam.richTextMetadata || {},
                            pages: exam.pages.map((page) => ({
                              ...page,
                              sections: page.sections.map((sect) =>
                                sect.id === section.id
                                  ? {
                                      ...sect,
                                      questions: [
                                        ...sect.questions,
                                        finalQuestion,
                                      ],
                                    }
                                  : sect
                              ),
                            })),
                          };

                          // Step 3: Calculate new total marks
                          const newTotalMarks = updatedExamData.pages.reduce(
                            (total, page) =>
                              total +
                              page.sections.reduce(
                                (sectionTotal, sect) =>
                                  sectionTotal +
                                  sect.questions.reduce(
                                    (questionTotal, question) =>
                                      questionTotal + (question.marks || 1),
                                    0
                                  ),
                                0
                              ),
                            0
                          );

                          // Step 4: Update exam metadata with new total marks
                          updatedExamData.metadata = {
                            ...updatedExamData.metadata,
                            totalMarks: newTotalMarks,
                          };
                          updatedExamData.score = newTotalMarks;

                          // ✅ CRITICAL: Update richTextMetadata for the new question
                          if (!updatedExamData.richTextMetadata[section.id]) {
                            updatedExamData.richTextMetadata[section.id] = {
                              instructions: null,
                              questions: [],
                            };
                          }

                          // Add the new question to richTextMetadata
                          updatedExamData.richTextMetadata[
                            section.id
                          ].questions.push({
                            questionId: questionId,
                            configuration: finalQuestion.richContent,
                          });

                          // Step 5: Update metadata using utility function
                          const metadataResult =
                            await updateExaminationTemplateWithMetadata(
                              updatedExamData,
                              exam.id,
                              fetchExamDetails
                            );

                          if (metadataResult.success) {
                            console.log("Question added successfully");
                          } else {
                            console.error(
                              "Failed to update metadata after adding question:",
                              metadataResult.error
                            );
                            // Still fetch fresh data even if metadata update failed
                            await fetchExamDetails();
                          }
                        } else {
                          console.error("Failed to add question:", response);
                          alert("Failed to add question. Please try again.");
                        }
                      } catch (error) {
                        console.error("Error adding question:", error);
                        alert("Failed to add question. Please try again.");
                      }
                    };

                    addQuestionToSectionInline();
                  }}
                >
                  <Plus size={16} />
                  Add First Question
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const organizeContentIntoPages = () => {
    const pageHeight = currentPageSize.height - 160;
    const questionBaseHeight = 75;
    const optionHeight = 25;
    const sectionHeaderHeight = 50;
    const examHeaderHeight = 200;
    const pageHeaderHeight = 20;
    const pagePadding = 30;
    const sectionInstructionsHeight = 35;
    const sectionMargin = 15;

    let organizedPages = [];
    let currentPageHeight = 0;
    let currentPage = null;
    let pageIndex = 0;

    // Helper function to create a new page
    const createNewPage = () => {
      const newPage = {
        id: `page-${Date.now()}-${pageIndex}`,
        padding: 20,
        borderStyle: "solid",
        pageBreakBefore: pageIndex > 0,
        sections: [],
      };

      // Set initial height based on page type
      if (pageIndex === 0) {
        currentPageHeight = examHeaderHeight + pagePadding; // First page with exam header
      } else {
        currentPageHeight = pageHeaderHeight + pagePadding; // Subsequent pages
      }

      pageIndex++;
      return newPage;
    };

    // Helper function to calculate question height
    const calculateQuestionHeight = (question) => {
      let height = questionBaseHeight;

      if (
        question.type === "with-options" &&
        question.options &&
        question.options.length > 0
      ) {
        height += question.options.length * optionHeight;
      }

      if (question.richContent && question.richContent.html) {
        const htmlLength = question.richContent.html.length;
        if (htmlLength > 200) {
          height += Math.floor(htmlLength / 100) * 15;
        }
      }

      return height;
    };

    // Helper function to check if content fits on current page
    const canFitOnCurrentPage = (additionalHeight) => {
      return currentPageHeight + additionalHeight <= pageHeight;
    };

    // Get all sections from exam
    console.log("Organizing content into pages...");
    console.log("Exam pages:", exam.pages);
    console.log("Exam sections:", exam.pages.flatMap((page) => page.sections));
    const allSections = exam.pages.flatMap((page) => page.sections);

    if (allSections.length === 0) {
      // If no sections, create a single page with just the header
      const emptyPage = createNewPage();
      organizedPages.push(emptyPage);
      return organizedPages;
    }

    // Start with first page
    currentPage = createNewPage();

    allSections.forEach((section, sectionIndex) => {
      // ✅ CRITICAL FIX: Skip completely empty sections
      // if (!section.questions || section.questions.length === 0) {
      //   return;
      // }

      let sectionHeight = sectionHeaderHeight;
      let sectionQuestionsForCurrentPage = [];
      let remainingQuestions = [...section.questions];

      // Add height for section instructions if they exist
      if (
        section.hasInstructions &&
        section.instructions &&
        section.instructions.plainText &&
        section.instructions.plainText.trim() !== "" &&
        section.instructions.plainText !== "Enter section instructions here..."
      ) {
        sectionHeight += sectionInstructionsHeight;
      }
      if (remainingQuestions.length === 0) {
        // For empty sections, just add them to the current page
        const totalHeightNeeded = sectionHeight + sectionMargin;

        if (canFitOnCurrentPage(totalHeightNeeded)) {
          // Empty section fits on current page
          currentPage.sections.push({
            ...section,
            questions: [],
          });
          currentPageHeight += totalHeightNeeded;
        } else {
          // Start a new page for the empty section
          if (currentPage.sections.length > 0) {
            organizedPages.push(currentPage);
            currentPage = createNewPage();
          }

          currentPage.sections.push({
            ...section,
            questions: [],
          });
          currentPageHeight += totalHeightNeeded;
        }

        // Add small margin after section
        currentPageHeight += sectionMargin / 2;
        return; // Continue to next section
      }
      // Process each question in the section
      while (remainingQuestions.length > 0) {
        const question = remainingQuestions[0];
        const questionHeight = calculateQuestionHeight(question);

        // Calculate total height needed for this question
        let totalHeightNeeded = questionHeight;

        // If this is the first question in a new section on this page, include section header
        if (sectionQuestionsForCurrentPage.length === 0) {
          totalHeightNeeded += sectionHeight + sectionMargin;
        }

        // Check if question fits on current page
        if (canFitOnCurrentPage(totalHeightNeeded)) {
          // Question fits, add it to current page
          sectionQuestionsForCurrentPage.push(remainingQuestions.shift());
          currentPageHeight +=
            sectionQuestionsForCurrentPage.length === 1
              ? totalHeightNeeded
              : questionHeight;
        } else {
          // Question doesn't fit on current page

          // If we have questions accumulated for this section on current page, add them
          if (sectionQuestionsForCurrentPage.length > 0) {
            currentPage.sections.push({
              ...section,
              questions: sectionQuestionsForCurrentPage,
              continuesOnNextPage: remainingQuestions.length > 0,
            });
            sectionQuestionsForCurrentPage = [];
          }

          // Start a new page
          organizedPages.push(currentPage);
          currentPage = createNewPage();

          // Now try to add the question to the new page
          totalHeightNeeded = questionHeight + sectionHeight + sectionMargin;

          if (canFitOnCurrentPage(totalHeightNeeded)) {
            // Question fits on new page
            sectionQuestionsForCurrentPage.push(remainingQuestions.shift());
            currentPageHeight += totalHeightNeeded;
          } else {
            // Question is too large for any page - force add it anyway
            console.warn(`Question is too large for page: ${question.id}`);
            sectionQuestionsForCurrentPage.push(remainingQuestions.shift());
            currentPageHeight += totalHeightNeeded;
          }
        }
      }

      // Add any remaining questions from this section to current page
      if (sectionQuestionsForCurrentPage.length > 0) {
        currentPage.sections.push({
          ...section,
          questions: sectionQuestionsForCurrentPage,
        });
      }

      // Add small margin after each section
      currentPageHeight += sectionMargin / 2;
    });

    // ✅ CRITICAL FIX: Only add the final page if it has content OR if no pages were created at all
    if (
      currentPage &&
      (currentPage.sections.length > 0 || organizedPages.length === 0)
    ) {
      organizedPages.push(currentPage);
    }

    // ✅ CRITICAL FIX: Ensure at least one page exists only if we truly have no content
    if (organizedPages.length === 0) {
      const emptyPage = createNewPage();
      organizedPages.push(emptyPage);
    }

    return organizedPages;
  };

  const organizedPages = organizeContentIntoPages();
  const totalMarks = calculateTotalMarks();

  // Create exam header component
  const renderExamHeader = () => (
    <div className="exam-header">
      <InlineEditableText
        value={exam.name}
        onChange={(value) => handleMetadataUpdate("name", value)}
        className="exam-heading"
        placeholder="Enter exam heading..."
      />
      <InlineEditableText
        value={exam.metadata.subheading}
        isEditable={false}
        onChange={(value) => handleMetadataUpdate("description", value)}
        className="exam-subheading"
        placeholder="Enter subheading..."
      />
      <InlineEditableText
        value={exam.metadata.subjectName}
        isEditable={false}
        onChange={(value) => handleMetadataUpdate("subjectName", value)}
        className="exam-subject"
        placeholder="Enter subject name..."
      />
      <div className="exam-details">
        <div className="exam-time">
          Time Allowed:{" "}
          <DurationPicker
            value={exam.duration}
            onChange={(value) => handleMetadataUpdate("timeAllowed", value)}
            className="time-input"
            placeholder="2.30 Hours"
          />
        </div>
        <div className="exam-marks">
          Maximum Marks:{" "}
          <InlineEditableNumber
            value={exam.score}
            onChange={(newMarks) =>
              handleMetadataUpdate("totalMarks", newMarks)
            }
            className="total-marks-input"
            min={1}
            max={10000}
            examData={exam} // ✅ NEW: Pass exam data
            isTotalMarks={true} // ✅ NEW: Flag as total marks field
          />
        </div>
      </div>

      <div className="exam-instructions">
        <strong>Instructions:</strong>{" "}
        <InlineEditableText
          value={
            exam.metadata.instructionsRichContent || {
              type: "rich-text",
              html: `<p>${
                exam.metadata.instructions || "Enter exam instructions..."
              }</p>`,
              plainText:
                exam.metadata.instructions || "Enter exam instructions...",
            }
          }
          onChange={(value) => handleMetadataUpdate("instructions", value)}
          className="instructions-text"
          placeholder="Enter exam instructions..."
          isRichText={true}
        />
      </div>
    </div>
  );

  return (
    <div className="canvas-container">
      <div
        ref={canvasRef}
        className="canvas"
        style={{
          width: "100%",
          maxWidth: "none",
        }}
      >
        {organizedPages.map((page, pageIndex) => (
          <div
            key={pageIndex}
            className={`page ${
              pageIndex < organizedPages.length - 1
                ? "page-with-bottom-margin"
                : ""
            }`}
            style={{
              padding: `${page.padding}px`,
              border:
                page.borderStyle !== "none"
                  ? `1px ${page.borderStyle} #e1e1e1`
                  : "none",
              pageBreakBefore: page.pageBreakBefore ? "always" : "auto",
              width: "100%",
              minHeight: currentPageSize.height - 80,
              boxSizing: "border-box",
            }}
          >
            {/* Add exam header only to first page */}
            {pageIndex === 0 && renderExamHeader()}

            <div className="page-header">
              <span className="page-number">
                Page {pageIndex + 1} of {organizedPages.length}
              </span>
            </div>

            {page.sections.map((section, sectionIndex) => {
              return (
                <React.Fragment key={generateId()}>
                  {renderSection(section, sectionIndex, pageIndex)}
                  {/* Section divider */}
                  {sectionIndex < page.sections.length - 1 && (
                    <div className="section-divider"></div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        ))}

        <div className="page-info-footer">
          <div className="total-pages-info">
            Total Pages: {organizedPages.length} | Paper Size:{" "}
            {currentPageSize.name} | Total Marks: {totalMarks}
          </div>
        </div>
      </div>

      <style jsx>{`
        .marks-error-tooltip {
          position: absolute;
          top: 100%;
          left: 50%; /* ✅ UPDATED: Center horizontally */
          transform: translateX(-50%); /* ✅ NEW: Center the tooltip */
          background: #ef4444;
          color: white;
          padding: 0.75rem;
          border-radius: 6px;
          font-size: 0.75rem;
          white-space: normal;
          max-width: 350px; /* ✅ UPDATED: Slightly smaller for better fit */
          min-width: 200px; /* ✅ NEW: Minimum width */
          z-index: 1000;
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          margin-top: 0.25rem;
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
          line-height: 1.4;
          word-wrap: break-word; /* ✅ NEW: Break long words */
        }

        /* ✅ NEW: Arrow pointing up to the input */
        .marks-error-tooltip::before {
          content: "";
          position: absolute;
          top: -6px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid #ef4444;
        }

        /* ✅ NEW: Responsive positioning for different screen sizes */
        @media (max-width: 768px) {
          .marks-error-tooltip {
            left: 0;
            right: 0;
            transform: none;
            max-width: calc(100vw - 2rem);
            margin: 0.25rem 1rem 0 1rem;
          }

          .marks-error-tooltip::before {
            left: 2rem;
            transform: none;
          }
        }

        /* ✅ NEW: For tooltips that might go off the right edge */
        .marks-error-tooltip.tooltip-right {
          left: auto;
          right: 0;
          transform: none;
        }

        .marks-error-tooltip.tooltip-right::before {
          left: auto;
          right: 2rem;
          transform: none;
        }
        .canvas-container {
          flex: 1;
          padding: 1rem;

          overflow-x: auto;
          overflow-y: auto;
          background: #f8fafc;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          min-height: 100vh;
        }
        // Add these styles within the existing <style jsx> block

        // Add these styles within the existing <style jsx> block in canvas.js

        .time-inputs-container {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .time-input::-webkit-outer-spin-button,
        .time-input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        .time-input[type="number"] {
          -moz-appearance: textfield;
        }

        .time-box {
          width: 1.5rem;
          height: 1.5rem;
          text-align: center;
          border: 1px solid #d1d5db;
          border-radius: 2px;
          font-size: 0.75rem;
          font-weight: 600;
          color: #1e293b;
          outline: none;
          padding: 0;
        }

        .time-box:focus {
          border-color: #3b82f6;
          background: #f0f9ff;
        }

        .time-box::-webkit-outer-spin-button,
        .time-box::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        .time-box[type="number"] {
          -moz-appearance: textfield;
        }

        .duration-editor span {
          font-size: 0.875rem;
          font-weight: 700;
          color: #374151;
          margin: 0 0.125rem;
        }

        .duration-save-btn {
          background: #10b981;
          color: white;
        }

        .duration-cancel-btn {
          background: #ef4444;
          color: white;
        }

        /* Responsive adjustments for duration picker */
        @media (max-width: 768px) {
          .time-inputs-container {
            gap: 0.5rem;
          }

          .time-input {
            width: 2.5rem;
            height: 2.5rem;
            font-size: 1rem;
          }
        }

        .total-marks-input {
          color: #1e293b;
          font-weight: 700;
          font-size: 1em;
          min-width: 3rem;
        }

        .total-marks-input:hover {
          background: #dbeafe;
          border-color: #93c5fd;
        }

        .canvas {
          background: #ffffff00;
          border-radius: 4px;

          margin: 10px;
          margin-bottom: 20px;

          width: 100%;
          min-width: 900px;
          font-family: "Times New Roman", Georgia, serif;
        }
        .inline-number-editor-container {
          position: relative;
          display: inline-block;
        }

        .inline-number-editor.error {
          border-color: #ef4444 !important;
          background: #fef2f2 !important;
        }

        .marks-error-tooltip {
          position: absolute;
          top: 100%;
          left: 0;
          background: #ef4444;
          color: white;
          padding: 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
          white-space: nowrap;
          z-index: 1000;
          display: flex;
          align-items: center;
          gap: 0.25rem;
          margin-top: 0.25rem;
          box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
        }

        .marks-error-tooltip::before {
          content: "";
          position: absolute;
          top: -4px;
          left: 8px;
          width: 0;
          height: 0;
          border-left: 4px solid transparent;
          border-right: 4px solid transparent;
          border-bottom: 4px solid #ef4444;
        }

        .exam-header {
          text-align: center;
          padding: 0.75rem 1rem 0.5rem; /* ✅ REDUCED: Smaller header padding */
          border-bottom: 2px solid #1e293b;
          margin-bottom: 0.75rem; /* ✅ REDUCED: Less margin */
          background: #ffffff;
        }

        .exam-heading {
          font-size: 1.2rem; /* ✅ REDUCED: Smaller to match PDF */
          text-transform: uppercase;
          letter-spacing: 0.5px; /* ✅ REDUCED: Less letter spacing */
          border-bottom: 1px solid #64748b;
          padding-bottom: 0.125rem; /* ✅ REDUCED: Less padding */
          margin-bottom: 0.375rem; /* ✅ REDUCED: Less margin */
          display: block !important;
          width: 100%;
        }

        .exam-subheading {
          font-size: 1.1rem;
          font-weight: 600;
          display: block !important;
          width: 100%;
          margin-bottom: 0.25rem;
        }

        .exam-subject {
          font-size: 0.9rem; /* ✅ REDUCED: Smaller font */
          font-weight: 600;
          margin-bottom: 0.5rem; /* ✅ REDUCED: Less margin */
          display: block !important;
          width: 100%;
        }

        .inline-editable {
          position: relative;
          cursor: pointer;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          transition: all 0.2s ease;
          border: 1px solid transparent;
          display: inline-block;
          min-width: 2rem;
        }

        .inline-editable:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
        }

        .inline-editable .edit-icon {
          opacity: 0;
          margin-left: 0.5rem;
          transition: opacity 0.2s ease;
          color: #64748b;
        }

        .inline-editable:hover .edit-icon {
          opacity: 1;
        }

        .inline-editor-container {
          display: inline-block;
        }

        .inline-editor {
          padding: 0.25rem 0.5rem;
          border: 2px solid #3b82f6;
          border-radius: 4px;
          background: #ffffff;
          font-size: inherit;
          font-weight: inherit;
          color: inherit;
          outline: none;
          min-width: 200px;
        }

        .inline-rich-editor-container {
          border: 2px solid #3b82f6;
          border-radius: 4px;
          background: #ffffff;
        }

        .inline-editor-actions {
          display: flex;
          gap: 0.25rem;
          padding: 0.5rem;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
        }

        .save-btn,
        .cancel-btn {
          padding: 0.25rem 0.5rem;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.875rem;
          transition: all 0.2s ease;
        }

        .save-btn {
          background: #10b981;
          color: white;
        }

        .save-btn:hover {
          background: #059669;
        }

        .cancel-btn {
          background: #6b7280;
          color: white;
        }

        .cancel-btn:hover {
          background: #4b5563;
        }

        .inline-editable-number {
          cursor: pointer;
          padding: 0.125rem 0.25rem;
          border-radius: 4px;
          transition: all 0.2s ease;
          border: 1px solid transparent;
          color: #3b82f6;
          font-weight: 600;
        }

        .inline-editable-number:hover {
          background: #dbeafe;
          border-color: #93c5fd;
        }

        .inline-number-editor {
          width: 3rem;
          padding: 0.125rem 0.25rem;
          border: 2px solid #3b82f6;
          border-radius: 4px;
          background: #ffffff;
          color: #3b82f6;
          font-weight: 600;
          outline: none;
        }

        .exam-details {
          display: flex;
          justify-content: space-between;
          margin: 0.5rem 0; /* ✅ REDUCED: Less margin */
          font-weight: 600;
          color: #1e293b;
          border-top: 1px solid #64748b;
          border-bottom: 1px solid #64748b;
          padding: 0.375rem 0; /* ✅ REDUCED: Less padding */
          font-size: 0.9rem; /* ✅ REDUCED: Smaller font */
        }

        .total-marks {
          color: #1e293b;
          font-weight: 700;
          font-size: 1em;
        }

        .time-input {
          font-weight: 700;
          color: #1e293b;
        }

        .exam-instructions {
          text-align: left;
          margin: 0.5rem 0 0; /* ✅ REDUCED: Less margin */
          padding: 0.375rem 0; /* ✅ REDUCED: Less padding */
          line-height: 1.3; /* ✅ REDUCED: Tighter line height */
          color: #1e293b;
          font-size: 0.85rem; /* ✅ REDUCED: Smaller font */
        }

        .page {
          background: #ffffff;
          position: relative;
          overflow: hidden;
          width: 100%;
          border: none;
          padding: 0.75rem 1rem; /* ✅ REDUCED: Smaller padding to match PDF */
          margin-bottom: 2rem; /* Keep spacing between pages for editing */
        }

        /* Add bottom margin between pages, but not after the last page */
        .page-with-bottom-margin {
          margin-bottom: 2rem;
          border-bottom: 2px dashed #94a3b8;
          padding-bottom: 1.5rem;
        }

        .page-header {
          display: flex;
          justify-content: flex-end;
          padding: 0;
          margin-bottom: 0.5rem; /* ✅ REDUCED: Less margin */
          border: none;
          background: transparent;
        }

        .page-number {
          font-size: 0.65rem; /* ✅ REDUCED: Smaller font to match PDF */
          color: #64748b;
          font-weight: 500;
          font-family: "Times New Roman", serif;
        }

        .section {
          margin: 0.5rem 0; /* ✅ REDUCED: Less margin */
          position: relative;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 1px solid transparent;
          border-radius: 3px;
          padding: 0.5rem; /* ✅ REDUCED: Less padding */
          background: #ffffff;
          width: 100%;
          box-sizing: border-box;
        }
        .section:hover:not(.section-selected):not(.has-editing-question) {
          background: #f8fafc;
          border-color: #e2e8f0;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }
        .section.has-editing-question {
          background: #fafafa;
          border-color: #e5e7eb;
        }

        .section:hover:not(.selected) {
          background: #f8fafc;
          border-color: #e2e8f0;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .section.section-selected {
          background: #eff6ff;
          border-color: #3b82f6;
          box-shadow: 0 1px 4px rgba(59, 130, 246, 0.15);
        }

        .section.animate-in {
          animation: slideInFade 0.3s ease-out forwards;
        }

        @keyframes slideInFade {
          from {
            opacity: 0;
            transform: translateY(-5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .section-header {
          margin-bottom: 0.375rem; /* ✅ REDUCED: Less margin */
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 0.125rem; /* ✅ REDUCED: Less padding */
        }

        .section-title {
          font-size: 0.95rem; /* ✅ REDUCED: Smaller font to match PDF */
          font-weight: 700;
          margin: 0;
          display: flex;
          align-items: center;
          color: #1e293b;
          cursor: pointer;
          transition: color 0.2s ease;
          font-family: "Times New Roman", serif;
        }

        .section-title:hover {
          color: #3b82f6;
        }

        .section-title-editor {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .section-title-input {
          font-size: 1.1rem;
          font-weight: 600;
          border: 2px solid #3b82f6;
          border-radius: 4px;
          padding: 0.25rem 0.5rem;
          background: #ffffff;
          color: #1e293b;
          flex: 1;
          outline: none;
        }

        .section-title-actions {
          display: flex;
          gap: 0.25rem;
        }

        .title-save-btn,
        .title-cancel-btn {
          padding: 0.25rem;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .title-save-btn {
          background: #10b981;
          color: white;
        }

        .title-save-btn:hover {
          background: #059669;
        }

        .title-cancel-btn {
          background: #ef4444;
          color: white;
        }

        .title-cancel-btn:hover {
          background: #dc2626;
        }

        .selection-indicator {
          background: #3b82f6;
          color: white;
          font-size: 10px;
          padding: 2px 8px;
          border-radius: 12px;
          margin-left: 12px;
          font-weight: 500;
        }

        .section-marks {
          font-size: 0.875rem;
          color: #64748b;
          margin-top: 0.25rem;
          font-style: italic;
        }

        .section-content {
          padding-left: 0.25rem;
          flex: 1;
          min-height: 40px;
          opacity: 1;
          visibility: visible;
          display: block;
        }

        .section-divider {
          height: 1px;
          background: #d1d5db;
          margin: 0.5rem 0; /* ✅ REDUCED: Less margin */
        }

        .question-item {
          display: flex;
          margin: 0.375rem 0; /* ✅ REDUCED: Less margin */
          position: relative;
          cursor: pointer;
          padding: 0.375rem; /* ✅ REDUCED: Less padding */
          border-radius: 3px;
          transition: all 0.2s ease;
          border: 1px solid transparent;
          line-height: 1.4; /* ✅ REDUCED: Tighter line height */
          width: 100%;
          box-sizing: border-box;
          font-family: "Times New Roman", serif;
        }

        .question-item:hover:not(.editing):not(.question-selected) {
          background: #f8fafc;
          border-color: #e2e8f0;
        }

        .question-item.selected {
          background: #fef3c7;
          border-color: #f59e0b;
          box-shadow: 0 1px 4px rgba(245, 158, 11, 0.15);
        }

        .question-item.editing {
          cursor: default;
          background: #f0f9ff;
          border: 2px solid #0ea5e9;
          padding: 0.25rem;
          width: 100%;
          overflow: visible;
          box-shadow: 0 2px 8px rgba(14, 165, 233, 0.15);
        }

        .question-item.animate-in {
          animation: slideInFade 0.3s ease-out forwards;
        }
        .question-number {
          font-weight: 700;
          margin-right: 0.5rem; /* ✅ REDUCED: Less margin */
          min-width: 1.5rem; /* ✅ REDUCED: Smaller width */
          color: #1e293b;
          font-size: 0.95rem; /* ✅ REDUCED: Smaller font */
          font-family: "Times New Roman", serif;
        }

        .question-content {
          flex: 1;
          width: 100%;
          min-width: 0;
        }

        .question-content-editing {
          flex: 1;
          width: 100%;
          min-width: 0;
          overflow: visible;
        }

        .editing-container {
          width: 100%;
          max-width: none;
          min-width: 0;
          overflow-x: auto;
          overflow-y: visible;
        }

        .question-text {
          margin-bottom: 0.375rem; /* ✅ REDUCED: Less margin */
          line-height: 1.4; /* ✅ REDUCED: Tighter line height */
          color: #1e293b;
          width: 100%;
          word-wrap: break-word;
          font-family: "Times New Roman", serif;
        }

        .question-options {
          margin: 0.375rem 0; /* ✅ REDUCED: Less margin */
          padding-left: 0.5rem; /* ✅ REDUCED: Less padding */
        }

        .option-item {
          display: flex;
          margin: 0.125rem 0; /* ✅ REDUCED: Less margin */
          align-items: flex-start;
        }

        .option-label {
          font-weight: 600;
          margin-right: 0.375rem; /* ✅ REDUCED: Less margin */
          min-width: 1.25rem; /* ✅ REDUCED: Smaller width */
          color: #1e293b;
          font-family: "Times New Roman", serif;
        }
        .option-text {
          flex: 1;
          line-height: 1.4;
          color: #1e293b;
          font-family: "Times New Roman", serif;
        }

        .question-marks {
          font-size: 0.8rem; /* ✅ REDUCED: Smaller font */
          color: #64748b;
          margin-top: 0.375rem; /* ✅ REDUCED: Less margin */
          text-align: right;
          font-style: italic;
          font-family: "Times New Roman", serif;
        }
        .page-info-footer {
          padding: 0.5rem;
          text-align: center;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          color: #64748b;
          font-size: 0.7rem;
          border-radius: 0 0 4px 4px;
          font-family: "Times New Roman", serif;
        }

        .total-pages-info {
          font-weight: 500;
        }

        /* Math and Editor Styles for Exam Paper */
        math-field {
          display: inline-block;
          width: 100%;
        }

        .math-inline.edit-mode {
          position: relative;
          display: inline-block;
          border: 2px solid #3b82f6 !important;
          border-radius: 6px !important;
          padding: 4px 24px 4px 8px !important;
          margin: 2px !important;
          background: rgba(59, 130, 246, 0.05) !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
        }

        .math-inline.edit-mode:hover {
          background: rgba(59, 130, 246, 0.1) !important;
          border-color: #2563eb !important;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2) !important;
        }

        .math-delete-btn {
          position: absolute;
          top: -8px;
          right: -8px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #ef4444 !important;
          color: white !important;
          border: 2px solid white !important;
          cursor: pointer !important;
          display: flex !important;
          align-items: center;
          justify-content: center;
          font-size: 12px !important;
          font-weight: bold !important;
          line-height: 1 !important;
          z-index: 10;
          transition: all 0.2s ease !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
        }

        .math-delete-btn:hover {
          background: #dc2626 !important;
          transform: scale(1.1);
          box-shadow: 0 3px 8px rgba(239, 68, 68, 0.3) !important;
        }

        .math-inline.edit-mode math-field {
          background: transparent !important;
          border: none !important;
          padding: 0 !important;
          margin: 0 !important;
        }

        .math-inline:not(.edit-mode) {
          background: transparent !important;
          padding: 2px !important;
          border: none !important;
          margin: 0 2px !important;
          cursor: default !important;
        }

        .math-inline:not(.edit-mode):hover {
          background: transparent !important;
          border: none !important;
        }

        .math-inline:not(.edit-mode) math-field {
          background: transparent !important;
          border: none !important;
          padding: 0 !important;
        }

        /* Section instructions styles */
        .section-instructions {
          margin: 0.5rem 0;
          transition: all 0.3s ease;
        }

        .section-instructions-editor {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 1rem;
        }

        .instructions-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .instructions-header label {
          font-weight: 600;
          color: #374151;
          font-size: 0.9rem;
          margin: 0;
        }

        .instructions-controls {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .checkbox-container {
          display: flex;
          align-items: center;
          cursor: pointer;
          font-size: 0.85rem;
          color: #6b7280;
          gap: 0.5rem;
        }

        .checkbox-container input[type="checkbox"] {
          margin: 0;
          margin-right: 0.5rem;
        }

        .checkmark {
          display: inline-block;
        }

        .instructions-content {
          margin-top: 0.75rem;
        }

        .section-instructions-display {
          background: #f0fdf4;
          border: 1px solid #dcfce7;
          border-radius: 6px;
          padding: 0.75rem;
        }

        .instructions-label {
          font-weight: 600;
          color: #166534;
          font-size: 0.85rem;
          margin-bottom: 0.5rem;
        }

        .instructions-text {
          color: #166534;
          font-size: 0.9rem;
          line-height: 1.5;
        }

        /* Responsive behavior for editing mode */
        @media (max-width: 1920px) {
          .canvas-container {
            padding: 1rem;
          }

          .canvas {
            min-width: 800px;
          }
        }

        @media (max-width: 1600px) {
          .canvas-container {
            padding: 1rem;
          }

          .canvas {
            min-width: 700px;
          }
        }

        @media (max-width: 1440px) {
          .canvas {
            min-width: 650px;
          }
        }

        @media (max-width: 1024px) {
          .canvas-container {
            padding: 0.75rem;
          }

          .canvas {
            min-width: 600px;
          }
        }

        @media (max-width: 768px) {
          .canvas-container {
            padding: 0.5rem;
            overflow-x: auto;
          }

          .canvas {
            margin: 0;
            border-radius: 2px;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
            min-width: 500px;
          }

          .exam-details {
            flex-direction: column;
            gap: 0.25rem;
          }

          .question-item {
            flex-direction: column;
            gap: 0.25rem;
          }

          .question-number {
            min-width: auto;
            margin-right: 0;
          }

          .question-item.editing {
            padding: 0.25rem;
          }

          .editing-container {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .page {
            padding: 0.75rem 1rem;
          }

          .section {
            margin: 0.5rem 0;
            padding: 0.5rem;
          }
        }

        .section.empty-section {
          border: 2px dashed #d1d5db;
          background: #f9fafb;
          min-height: 120px;
          display: flex;
          flex-direction: column;
          transition: all 0.3s ease;
          opacity: 1;
          visibility: visible;
        }

        .section.empty-section:hover {
          border-color: #3b82f6;
          background: #f0f9ff;
          box-shadow: 0 2px 6px rgba(59, 130, 246, 0.1);
        }

        .section.empty-section.selected {
          border-color: #3b82f6;
          background: #f0f9ff;
          border-style: solid;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
        }

        .empty-section-placeholder {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.5rem;
          text-align: center;
          opacity: 1;
          visibility: visible;
          min-height: 80px;
        }

        .empty-section-placeholder.selected {
          background: rgba(59, 130, 246, 0.05);
          border-radius: 8px;
        }

        .empty-section-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          max-width: 300px;
          opacity: 1;
          visibility: visible;
        }

        .empty-section-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: #e5e7eb;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9ca3af;
          transition: all 0.3s ease;
          opacity: 1;
          visibility: visible;
        }

        .section.empty-section:hover .empty-section-icon {
          background: #dbeafe;
          color: #3b82f6;
          transform: scale(1.05);
        }

        .empty-section-text {
          opacity: 1;
          visibility: visible;
        }

        .empty-section-text h5 {
          margin: 0 0 0.25rem 0;
          font-size: 0.9rem;
          font-weight: 600;
          color: #374151;
          opacity: 1;
          visibility: visible;
        }

        .empty-section-text p {
          margin: 0;
          font-size: 0.8rem;
          color: #6b7280;
          line-height: 1.4;
          opacity: 1;
          visibility: visible;
        }

        .add-question-placeholder-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.8rem;
          font-weight: 500;
          transition: all 0.2s ease;
          box-shadow: 0 1px 4px rgba(59, 130, 246, 0.2);
          opacity: 1;
          visibility: visible;
        }

        .add-question-placeholder-btn:hover {
          background: #2563eb;
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(59, 130, 246, 0.3);
        }

        .add-question-placeholder-btn:active {
          transform: translateY(0);
        }

        .question-item.new-question {
          animation: slideInFromRight 0.4s ease-out;
        }

        @keyframes slideInFromRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @media print {
          .page {
            padding: 8mm 5mm !important; /* ✅ CRITICAL: Match PDF padding exactly */
            margin: 0 !important;
            page-break-after: always;
            border: none !important;
            box-shadow: none !important;
          }

          .page:last-child {
            page-break-after: avoid;
          }

          .exam-header {
            padding: 4mm 6mm 3mm !important; /* ✅ CRITICAL: Match PDF header padding */
            margin-bottom: 4mm !important;
          }

          .section {
            margin: 2mm 0 !important; /* ✅ CRITICAL: Match PDF section spacing */
            padding: 2mm !important;
          }

          .question-item {
            margin: 1.5mm 0 !important; /* ✅ CRITICAL: Match PDF question spacing */
            padding: 1.5mm !important;
          }

          .duration-display {
            cursor: pointer;
            color: #1e293b;
            font-weight: 700;
            font-size: 1em;
            position: relative;
            display: inline-block;
            min-width: 6rem;
            padding: 0; /* Remove unnecessary padding */
            border: none; /* Remove unnecessary border */
            background: transparent; /* Remove background */
            transition: color 0.2s ease;
          }

          .duration-display:hover {
            color: #3b82f6;
          }

          .duration-display .edit-icon {
            opacity: 0;
            margin-left: 0.5rem;
            transition: opacity 0.2s ease;
            color: #64748b;
          }

          .duration-display:hover .edit-icon {
            opacity: 1;
          }

          /* Duration Editor (editing state) */
          .duration-editor {
            display: inline-flex;
            align-items: center;
            gap: 0.125rem;
            padding: 0.25rem;
            background: #ffffff;
            border: 2px solid #3b82f6;
            border-radius: 4px;
            box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
          }

          .time-box {
            width: 1.5rem;
            height: 1.5rem;
            text-align: center;
            border: 1px solid #d1d5db;
            border-radius: 2px;
            font-size: 0.75rem;
            font-weight: 600;
            color: #1e293b;
            outline: none;
            padding: 0;
            background: #ffffff;
            transition: all 0.2s ease;
          }

          .time-box:focus {
            border-color: #3b82f6;
            background: #f0f9ff;
            box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
          }

          .time-box::-webkit-outer-spin-button,
          .time-box::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }

          .time-box[type="number"] {
            -moz-appearance: textfield;
          }

          .duration-editor span {
            font-size: 0.875rem;
            font-weight: 700;
            color: #374151;
            margin: 0 0.125rem;
          }

          /* Updated CSS for InlineEditableText - Add these styles to the existing <style jsx> block in canvas.js */

          .inline-editable {
            position: relative;
            cursor: pointer;
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
            transition: all 0.2s ease;
            border: 1px solid transparent;
            display: inline-block;
            min-width: 2rem;
          }

          /* ✅ UPDATED: Only apply hover effects when editable */
          .inline-editable:not(.non-editable):hover {
            background: #f8fafc;
            border-color: #cbd5e1;
          }

          /* ✅ NEW: Non-editable state styling */
          .inline-editable.non-editable {
            cursor: default;
            padding: 0.25rem 0.5rem;
          }

          .inline-editable.non-editable:hover {
            background: transparent;
            border-color: transparent;
          }

          .inline-editable .edit-icon {
            opacity: 0;
            margin-left: 0.5rem;
            transition: opacity 0.2s ease;
            color: #64748b;
          }

          /* ✅ UPDATED: Only show edit icon on hover for editable elements */
          .inline-editable:not(.non-editable):hover .edit-icon {
            opacity: 1;
          }

          /* ✅ NEW: Ensure edit icon never shows for non-editable elements */
          .inline-editable.non-editable .edit-icon {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default Canvas;
